import { zodResolver } from '@hookform/resolvers/zod';
import { verifyEmailSchema } from '@willnq/shared/validation/authSchemas';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import type { z } from 'zod';

import { ROUTES } from '@/constants/routes';
import type { VerifyEmailState } from '@/features/identity/types/identity.types';
import { useToast } from '@/hooks/useToast';
import { authService } from '@/services/authService';
import { parseRestError } from '@/utils/errorUtils';

type VerifyEmailFormValues = z.infer<typeof verifyEmailSchema>;

/** Client-side cooldown between resend requests — the backend is anti-enumeration and stateless
 * about resend rate today (the gateway rate limiter is the eventual server-side control), so this
 * keeps a well-behaved user from hammering the endpoint. */
const RESEND_COOLDOWN_SECONDS = 30;

/** Drives `VerifyEmailForm`, including the "resend code" action backed by `/auth/resend-verification`. */
export function useVerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess } = useToast();
  const prefillEmail = (location.state as VerifyEmailState | null)?.email ?? '';
  const [serverError, setServerError] = useState<string | null>(null);
  const [verified, setVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const form = useForm<VerifyEmailFormValues>({
    resolver: zodResolver(verifyEmailSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { email: prefillEmail, otp: '' },
  });

  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
    },
    [],
  );

  const startCooldown = useCallback(() => {
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    timerRef.current = setInterval(() => {
      setResendCooldown((remaining) => {
        if (remaining <= 1 && timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        return Math.max(0, remaining - 1);
      });
    }, 1000);
  }, []);

  const onSubmit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      await authService.verifyEmail(values);
      setVerified(true);
      setTimeout(() => navigate(ROUTES.login), 1500);
    } catch (error) {
      // Routed through `serverError` (an <Alert>), not `form.setError('otp', ...)` — the shared
      // `OtpField` component only shows a red border for a field error, it never renders the
      // message text, so setError alone would leave the user with no visible explanation.
      setServerError(parseRestError(error).message);
    }
  });

  const resend = useCallback(async () => {
    const email = form.getValues('email');
    if (!email || resendCooldown > 0) return;
    setServerError(null);
    try {
      await authService.resendVerification(email);
      // Anti-enumeration: the backend always succeeds, so the message is deliberately neutral.
      showSuccess('If your account still needs verification, a new code is on its way.');
      startCooldown();
    } catch (error) {
      setServerError(parseRestError(error).message);
    }
  }, [form, resendCooldown, showSuccess, startCooldown]);

  return {
    form,
    onSubmit,
    serverError,
    verified,
    hasPrefillEmail: Boolean(prefillEmail),
    resend,
    resendCooldown,
  };
}
